import type { RecurrenceRule } from '../model/recurrence';
import { WEEKDAY_SHORT } from '../lib/parseQuickAdd';
import { describeRecurrenceRule } from '../lib/parseQuickAdd';

export interface RecurrenceFieldsProps {
  enabled: boolean;
  rule: RecurrenceRule | null;
  onToggle: (enabled: boolean) => void;
  onRuleChange: (rule: RecurrenceRule) => void;
}

const DEFAULT_RULE: RecurrenceRule = {
  frequency: 'daily',
  interval: 1,
  end_date: null,
};

/** Поля правила повторения в панели редактирования + понятное превью. */
export function RecurrenceFields({ enabled, rule, onToggle, onRuleChange }: RecurrenceFieldsProps) {
  const current = rule ?? DEFAULT_RULE;
  const preview = enabled
    ? describeRecurrenceRule({
        frequency: current.frequency,
        interval: current.interval,
        weekdays: current.weekdays ?? [],
        end_date: current.end_date ?? null,
      })
    : null;

  function patch(partial: Partial<RecurrenceRule>): void {
    onRuleChange({ ...current, ...partial });
  }

  return (
    <div className="space-y-2">
      <label className="flex cursor-pointer items-center gap-2 text-sm text-tg-text">
        <input
          type="checkbox"
          className="h-4 w-4 accent-tg-accent"
          checked={enabled}
          onChange={(event) => {
            onToggle(event.target.checked);
          }}
        />
        Повторять задачу
      </label>

      {enabled ? (
        <div className="space-y-2 rounded-xl border border-tg-border-soft bg-tg-bg p-3">
          {preview !== null ? (
            <p className="text-sm font-medium text-tg-accent" data-testid="recurrence-preview">
              {preview}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-1.5">
            {(
              [
                { value: 'daily', label: 'Дни' },
                { value: 'weekly', label: 'Недели' },
                { value: 'monthly', label: 'Месяцы' },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  current.frequency === option.value
                    ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                    : 'border-tg-border-soft text-tg-text-secondary'
                }`}
                onClick={() => {
                  patch({ frequency: option.value });
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm text-tg-text-secondary">
            Интервал
            <input
              type="number"
              min={1}
              step={1}
              className="tg-input h-9 min-h-9 w-24"
              value={current.interval}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (Number.isFinite(value) && value >= 1) {
                  patch({ interval: Math.trunc(value) });
                }
              }}
            />
            <span className="text-xs text-tg-text-muted">
              {current.frequency === 'daily'
                ? 'дней'
                : current.frequency === 'weekly'
                  ? 'недель'
                  : 'месяцев'}
            </span>
          </label>

          {current.frequency === 'weekly' ? (
            <div>
              <p className="tg-label mb-1">Дни недели</p>
              <div className="flex flex-wrap gap-1">
                {WEEKDAY_SHORT.map((label, day) => {
                  const selected = (current.weekdays ?? []).includes(day);
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`rounded-full border px-2 py-0.5 text-xs ${
                        selected
                          ? 'border-tg-accent/50 bg-tg-accent-soft text-tg-accent'
                          : 'border-tg-border-soft text-tg-text-muted'
                      }`}
                      onClick={() => {
                        const set = new Set(current.weekdays ?? []);
                        if (selected) {
                          set.delete(day);
                        } else {
                          set.add(day);
                        }
                        patch({ weekdays: [...set].sort((a, b) => a - b) });
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <label className="block text-sm text-tg-text-secondary">
            Дата окончания (необязательно)
            <input
              type="date"
              className="tg-input mt-1 h-9 min-h-9"
              value={current.end_date ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                patch({ end_date: value.length === 0 ? null : value });
              }}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}
